import * as React from 'react'
import { v4 as uuid } from 'uuid'
import { NamespacedStorage } from '../../../../client/utils/namespaced-storage'
import {
  PUSH_MODIFICATION_ERRORS,
  CLEAR_MODIFICATION_ERRORS,
  CartAction,
  ModifiedCartItem
} from './cart-actions'
import { getCartMeta } from '../../../../client/api/cart'
import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useClearUrlParams } from './useClearUrlParams'
import { useCartPersistence } from './useCartPersistence'
import { useCustomerAuthCart } from './useCustomerAuthCart'
import { useMarketplaceCart } from './useMarketplaceCart'
import { Cart, CartMetadataResponse } from '../../../../client/types/cart'

interface CartState {
  modificationErrors: {
    id?: string
    items: ModifiedCartItem[]
    message?: string
  }[]
}

const initialCartState: CartState = {
  modificationErrors: []
}

const cartStateReducer = (state: CartState, action: CartAction): CartState => {
  switch (action.type) {
    case PUSH_MODIFICATION_ERRORS:
      const newErrorsWithUuid =
        action.modificationErrors?.map((obj) => ({
          ...obj,
          id: uuid() as string
        })) || []
      return {
        ...state,
        modificationErrors: [...state.modificationErrors, ...newErrorsWithUuid]
      }
    case CLEAR_MODIFICATION_ERRORS:
      return {
        ...state,
        modificationErrors: []
      }
    default:
      throw new Error(
        `action type ${action.type} is not defined in the cartState: cartModificationsReducer`
      )
  }
}

interface CartContextType {
  restaurantGuid: string
  cartGuid?: string
  updateCartCache: (cart: Cart) => void
  deleteCartGuid: (fastLinkName: string | undefined) => void
  cartState: CartState
  dispatch: React.Dispatch<CartAction>
  willEarnLoyalty: boolean
  setWillEarnLoyalty: React.Dispatch<boolean>
  optedInTo3pAccountLookup: boolean
  setOptedInTo3pAccountLookup: React.Dispatch<boolean>
  initialCartNotFound: boolean
  setInitialCartNotFound: React.Dispatch<boolean>
  initialCartMetadata: CartMetadataResponse | undefined
  error: any
  loading: boolean
  refetch: () => void
  cart?: Cart
  warnings: any
  cartTotal: number
  cartIsEmpty: boolean
  numberOfSelections: number
  isEditingDisabled: boolean
  isSharedCart: boolean
  setIsSharedCart: (value: boolean) => void
}

//@ts-ignore
const CartContext = React.createContext<CartContextType>({})

interface CartProviderProps {
  restaurantStorage: NamespacedStorage
  restaurantGuid: string
  fastLinkName: string | undefined
  children: React.ReactNode
}

export const CartProvider: React.FC<CartProviderProps> = ({
  restaurantStorage,
  restaurantGuid,
  fastLinkName,
  children
}) => {
  const { search } = useLocation()
  const queryParams = new URLSearchParams(search)
  const clearUrlParams = useClearUrlParams()
  const [initialCartNotFound, setInitialCartNotFound] = useState(false)
  const [initialCartMetadata, setInitialCartMetadata] =
    useState<CartMetadataResponse>()

  // Store the initial cart GUID from URL to use later if cart is not found
  const [urlCartGuid] = useState(() => queryParams.get('cart'))

  const [optedInTo3pAccountLookup, setOptedInTo3pAccountLookup] =
    useState(false)
  const [willEarnLoyalty, setWillEarnLoyalty] = useState(false)

  const {
    cartGuid,
    cart,
    isFetching,
    error,
    refetch,
    updateCartGuid,
    updateCartCache,
    deleteCartGuid,
    getOtherCachedCartGuid,
    isSharedCart,
    setIsSharedCart
  } = useCartPersistence({
    restaurantStorage,
    fastLinkName,
    setOptedInTo3pAccountLookup
  })

  // Calculate derived values for cart display
  const warnings = cart?.warnings
  const check = cart?.order.checks[0]
  const numberOfSelections = check?.selections.length || 0
  const cartIsEmpty = numberOfSelections === 0
  const cartTotal = !cartIsEmpty ? check?.totalAmount || 0 : 0
  const loading = isFetching
  const isEditingDisabled = !!cart?.invalidExternalIds?.length

  useEffect(() => {
    // Handle shared cart case
    const initialCartGuid = queryParams.get('cart')
    if (initialCartGuid) {
      updateCartGuid(initialCartGuid, fastLinkName)
      clearUrlParams(['cart'])
      setIsSharedCart(true)
    } else {
      // Handle cart GUID loading from storage
      const cartGuidToLoad = cartGuid || getOtherCachedCartGuid(fastLinkName)
      if (cartGuidToLoad && cartGuidToLoad !== cartGuid) {
        updateCartGuid(cartGuidToLoad, fastLinkName)
      }
      setInitialCartMetadata(undefined)
    }
  }, [fastLinkName])

  // Handle cart loading success/error from React Query
  useEffect(() => {
    if (cart) {
      setInitialCartMetadata({ metadata: cart.metadata })
      updateCartCache(cart)
    } else if (error?.status === 404 && urlCartGuid) {
      // Handle cart not found case
      setInitialCartNotFound(true)
      getCartMeta(urlCartGuid)
        .then((meta) => setInitialCartMetadata(meta))
        .catch(() => setInitialCartMetadata(undefined))
    }
  }, [cart, error, urlCartGuid, updateCartCache, loading])

  // Sync customer auth data with cart
  useCustomerAuthCart({ cartGuid })

  // Handle marketplace cart creation
  useMarketplaceCart({ cartGuid, updateCartCache })

  const [cartState, dispatch] = React.useReducer(
    cartStateReducer,
    initialCartState
  )

  useEffect(() => {
    if (!initialCartNotFound) {
      setInitialCartMetadata(undefined)
    }
  }, [initialCartNotFound])

  const context = {
    restaurantGuid,
    cartGuid,
    updateCartCache,
    deleteCartGuid,
    cartState,
    dispatch,
    optedInTo3pAccountLookup,
    setOptedInTo3pAccountLookup,
    willEarnLoyalty,
    setWillEarnLoyalty,
    initialCartNotFound,
    setInitialCartNotFound,
    initialCartMetadata,
    error,
    loading,
    refetch,
    cart,
    warnings,
    cartTotal,
    cartIsEmpty,
    numberOfSelections,
    isEditingDisabled,
    isSharedCart,
    setIsSharedCart
  }

  return <CartContext.Provider value={context}>{children}</CartContext.Provider>
}

export const useCart = () => React.useContext<CartContextType>(CartContext)
